import React, { useEffect } from 'react';

import Stepper from '../../component/Stepper/Stepper';
import AppHeader from '../../component/AppHeader/app-header';
import PricingSection from './components/PricingSection';
import { useStepper } from '../../hooks/useStepper';
import PageLoader from '../../component/PageLoader';
import { useSubscriptionQuery } from '../../hooks/useSubscriptionQuery';
import { useDispatch, useSelector } from 'react-redux';
import { setBillingType, setPlan } from '../../store/reducers/subscriptionSlice/subscriptionSlice';
import { usePricingPlans } from '../../hooks/usePricingPlans';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useSubUrl from '../../hooks/useSubUrl';

const SubscriptionPlan = () => {
	const { steps, currentStep, goToStep } = useStepper();
	const { plans, loading } = usePricingPlans();
	const { billing, updateQuery, plan: selectedPlanKey } = useSubscriptionQuery();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const subscription = useSelector((state) => state.userInfo?.userInfo?.data?.subscription);
	const suburl = useSubUrl();
	const { suburl: suburlParam } = useParams();
	const [searchParams] = useSearchParams();

	// Reachable both as bare /subscription-plan (brand-new user, no
	// workspace yet) and /user/:suburl/subscription-plan (existing user
	// browsing plans again). If we know a suburl but the URL doesn't show
	// it, move to the scoped URL, same query string.
	useEffect(() => {
		if (suburl && !suburlParam) {
			navigate(`/user/${suburl}/subscription-plan?${searchParams.toString()}`, { replace: true });
		}
	}, [suburl, suburlParam, searchParams, navigate]);

	// Once plans load, sync the plan named in the URL (if any) and the
	// billing period into subscriptionSlice — same behavior as before, just
	// reacting to the shared hook's data instead of owning its own fetch.
	useEffect(() => {
		if (!plans.length) return;

		if (selectedPlanKey) {
			const selectedPlan = plans.find((item) => item.key === selectedPlanKey);

			if (selectedPlan) {
				dispatch(
					setPlan({
						id: selectedPlan?._id,
						key: selectedPlan?.key,
						title: selectedPlan?.name,
						description: selectedPlan?.meta?.description,
						billing: selectedPlan?.billing || [],
					}),
				);
			}
		}

		dispatch(setBillingType(billing || 'monthly'));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [plans, selectedPlanKey]);

	useEffect(() => {
		// `subscription.status` is the human display string ("Active",
		// "Cancels at end date", etc. — see getDisplayStatus on the backend),
		// not a lowercase enum, so don't compare against it.
		//
		// `is_active` alone is still wrong: a Free subscription is created with
		// status 'active', so Free users were redirected away from the plan
		// list — the very page they need in order to upgrade. Require a PAID
		// active plan before bouncing.
		// A complimentary plan (given free by staff) can still buy a plan here.
		if (subscription?.is_active && !subscription?.is_free && !subscription?.is_complimentary) {
			// Same as checkout.jsx: if this user already has a workspace,
			// send them straight into it (subdomain-scoped URL) instead of
			// the generic, subdomain-less /payment-success page.
			navigate(suburl ? `/user/${suburl}/settings/my-subscription` : '/payment-success', {
				replace: true,
			});
		}
	}, [subscription, suburl, navigate]);

	return (
		<>
			<AppHeader title="Subscription Plan" />
			<main className="main-wrapper">
				<div className="subscription-layout">
					<Stepper
						steps={steps}
						currentStep={currentStep}
						onStepClick={goToStep}
					/>

					{loading ? (
						<PageLoader />
					) : (
						<PricingSection
							billing={billing}
							setBilling={(value) =>
								updateQuery({
									billing: value,
									step: 'select-plan',
								})
							}
							plans={plans}
						/>
					)}
				</div>
			</main>
		</>
	);
};

export default SubscriptionPlan;
